"use client"

import { useState, useEffect, useRef } from "react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "./MapView.css"
import { Navigation } from "lucide-react"
import "./PlanningMapView.css"

const PlanningMapView = ({
  places,
  onBoundsChange,
  onPlanPlaces,
  selectedDate,
  placeTypes = {},
  showPlannedOnly = false,
}) => {
  const [map, setMap] = useState(null)
  const mapRef = useRef(null)
  const markersRef = useRef({})
  const currentLocationMarkerRef = useRef(null)
  const mapContainerRef = useRef(null)
  const [visiblePlaces, setVisiblePlaces] = useState(places)
  const [visibleCount, setVisibleCount] = useState(places.length)
  const [isLocating, setIsLocating] = useState(false)
  const [currentLocation, setCurrentLocation] = useState(null)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const [isShowingUserLocation, setIsShowingUserLocation] = useState(false)

  useEffect(() => {
    if (isSafari) {
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        iconUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    } else {
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "/images/default.png",
        iconUrl: "/images/default.png",
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    }
  }, [])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
    })

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map)

    mapRef.current = map

    map.on("moveend", () => {
      handleMapMove()
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!mapRef.current || !places.length) return

    Object.values(markersRef.current).forEach((marker) => {
      mapRef.current?.removeLayer(marker)
    })
    markersRef.current = {}

    places.forEach((place, index) => {
      const marker = createMarkerForPlace(place)
      marker.addTo(mapRef.current)
      markersRef.current[place.id] = marker
    })

    if (places.length > 0) {
      const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
      mapRef.current.fitBounds(bounds, { padding: [20, 20] })
    }

    handleMapMove()
  }, [places])

  useEffect(() => {
    Object.entries(markersRef.current).forEach(([placeId, marker]) => {
      const place = places.find((p) => p.id === placeId)
      if (place) {
        updateMarkerAppearance(marker, place)
      } else {
        console.warn(`Could not find place with ID: ${placeId}`)
      }
    })
  }, [placeTypes, places])

  const handleLocationClick = () => {
    if (isShowingUserLocation) {
      if (mapRef.current && places.length > 0) {
        const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
        mapRef.current.fitBounds(bounds, { padding: [20, 20] })
        setIsShowingUserLocation(false)
      }
    } else {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by this browser.")
        return
      }

      setIsLocating(true)

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const userLocation = [latitude, longitude]

          setCurrentLocation({ lat: latitude, lng: longitude })
          setIsLocating(false)
          setIsShowingUserLocation(true)

          if (mapRef.current) {
            if (currentLocationMarkerRef.current) {
              mapRef.current.removeLayer(currentLocationMarkerRef.current)
            }

            const currentLocationIcon = L.divIcon({
              html: `
                <div class="current-location-marker">
                  <div class="location-pulse"></div>
                  <div class="location-dot"></div>
                </div>
              `,
              className: "current-location-icon",
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            })

            currentLocationMarkerRef.current = L.marker(userLocation, {
              icon: currentLocationIcon,
            })
              .addTo(mapRef.current)
              .bindPopup("📍 Your current location")

            mapRef.current.setView(userLocation, 16, {
              animate: true,
              duration: 1,
            })
          }
        },
        (error) => {
          setIsLocating(false)
          let errorMessage = "Unable to get your location. "

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Location access was denied. Please enable location permissions and try again."
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location information is unavailable."
              break
            case error.TIMEOUT:
              errorMessage += "Location request timed out."
              break
            default:
              errorMessage += "An unknown error occurred."
              break
          }

          alert(errorMessage)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        },
      )
    }
  }

  const createMarkerForPlace = (place) => {
    const placeKey = `${place.lat},${place.lng}`
    const placeType = placeTypes[placeKey]

    let icon
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    if (isLocalhost || !placeType) {
      icon = new L.Icon.Default()
    } else {
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        icon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        icon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        icon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        icon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        icon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        icon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        icon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        icon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        icon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        icon = new L.Icon.Default()
      }
    }

    const marker = L.marker([place.lat, place.lng], {
      icon,
    })

    const popupContent = createPlanningPopupContent(place)
    marker.bindPopup(popupContent)

    return marker
  }

  const createPlanningPopupContent = (place) => {
    return `
      <div class="place-popup">
        <h3>${place.name}</h3>
        <div class="popup-actions">
          <button onclick="window.planningMapPlanPlace && window.planningMapPlanPlace('${place.id}')" 
                  class="popup-btn" 
                  ${!selectedDate ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ""}>
            📅 Plan This Place
          </button>
          ${!selectedDate ? '<p style="font-size: 12px; color: #666; margin: 4px 0 0 0;">Select a date first</p>' : ""}
        </div>
      </div>
    `
  }

  const updateMarkerAppearance = (marker, place) => {
    const placeKey = `${place.lat},${place.lng}`
    const placeType = placeTypes[placeKey]

    let baseIcon
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    if (isLocalhost || !placeType) {
      baseIcon = new L.Icon.Default()
    } else {
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        baseIcon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        baseIcon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        baseIcon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        baseIcon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        baseIcon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        baseIcon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        baseIcon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        baseIcon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        baseIcon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        baseIcon = new L.Icon.Default()
      }
    }

    marker.setIcon(baseIcon)

    const popupContent = createPlanningPopupContent(place)
    marker.setPopupContent(popupContent)
  }

  const handleMapMove = () => {
    if (!mapRef.current) return

    const bounds = mapRef.current.getBounds()
    const filtered = places.filter((place) => bounds.contains([place.lat, place.lng]))

    setVisiblePlaces(filtered)
    setVisibleCount(filtered.length)

    if (onBoundsChange) {
      onBoundsChange(filtered)
    }
  }

  const handlePlanVisiblePlaces = () => {
    if (visiblePlaces.length === 0) {
      alert("No places visible in current map area. Please zoom out or pan to include some locations.")
      return
    }

    if (!selectedDate) {
      alert("Please select a date first.")
      return
    }

    onPlanPlaces(visiblePlaces, selectedDate)
  }

  const handlePlanSinglePlace = (placeId) => {
    const place = places.find((p) => p.id === placeId)
    if (place && onPlanPlaces && selectedDate) {
      onPlanPlaces([place], selectedDate)
    }
  }

  useEffect(() => {
    window.planningMapPlanPlace = handlePlanSinglePlace
    return () => {
      delete window.planningMapPlanPlace
    }
  }, [places, onPlanPlaces, selectedDate])

  useEffect(() => {
    if (mapRef.current && places.length > 0) {
      setTimeout(() => {
        handleMapMove()
      }, 50)
    }
  }, [places])

  return (
    <div className="map-view">
      <div className="map-container-wrapper">
        <div ref={mapContainerRef} className="leaflet-map" />

        <div className="floating-controls-plan">
          <button
            onClick={handleLocationClick}
            disabled={isLocating}
            className={`floating-btn location-btn ${isShowingUserLocation ? "active" : ""}`}
            title={isShowingUserLocation ? "Show all places" : "My Location"}
          >
            <Navigation
              size={20}
              style={{ color: isShowingUserLocation ? "white" : "#666" }}
              className={isLocating ? "spinning" : ""}
            />
          </button>
        </div>

        <div className={`planning-controls ${showPlannedOnly ? "hide-plan-button" : ""}`}>
          <button
            onClick={handlePlanVisiblePlaces}
            className="planning-btn"
            disabled={visibleCount === 0 || !selectedDate}
            style={{
              background: visibleCount > 0 && selectedDate ? "#10b981" : "#9ca3af",
            }}
          >
            {selectedDate ? `Plan these ${visibleCount}?` : "Select a Date First"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlanningMapView
